// src/components/community/CommunitySubPortal.tsx
// ERUE Festival Suite — Community Sub-Portal Public View
// DROP 8a — Full implementation
// See full code in chat session

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Crown, Users, Calendar, Music, Camera, FileText,
  Clock, Phone, MapPin, Star, Loader2, Lock, Edit3, Share2,
  Play, Pause, Volume2, VolumeX, Megaphone, BookOpen, Globe,
  Mail, Video, Mic, ExternalLink, X, Image
} from 'lucide-react';

interface Props {
  villageId: string;
  villageName: string;
  onBack: () => void;
  onAdminAccess?: () => void;
  isAdmin?: boolean;
}

const CommunitySubPortal: React.FC<Props> = ({
  villageId, villageName, onBack, onAdminAccess, isAdmin
}) => {
  const [portal, setPortal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [galleryFilter, setGalleryFilter] = useState<string>('ALL');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shareMsg, setShareMsg] = useState('');

  const fetchPortal = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
      const res = await fetch(`/api/community-portals/${villageId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.success) setPortal(data.portal);
      else setPortal(null);
    } catch {
      setPortal(null);
    } finally {
      setLoading(false);
    }
  }, [villageId]);

  useEffect(() => { fetchPortal(); }, [fetchPortal]);

  // Auto-slideshow
  useEffect(() => {
    const featured = (portal?.media || []).filter(
      (m: any) => m.isFeatured && m.mediaType === 'IMAGE'
    );
    if (featured.length <= 1) return;
    const id = setInterval(() => setCurrentSlide(s => (s + 1) % featured.length), 5000);
    return () => clearInterval(id);
  }, [portal]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareMsg('Link copied!');
      setTimeout(() => setShareMsg(''), 2000);
    } catch { setShareMsg('Copy failed'); }
  };

  const TABS = [
    { id: 'home',     label: 'Home',     icon: Crown },
    { id: 'heritage', label: 'Heritage', icon: BookOpen },
    { id: 'gallery',  label: 'Gallery',  icon: Camera },
    { id: 'news',     label: 'News',     icon: FileText },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'team',     label: 'Team',     icon: Users },
    { id: 'contact',  label: 'Contact',  icon: Phone },
  ];

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:"#070D0A", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div className="text-center">
          <Loader2 style={{ width:48, height:48, color:"#C9A84C", margin:"0 auto 16px", animation:"spin 1s linear infinite" }} />
          <p style={{ color:"#C9A84C", fontWeight:600 }}>Loading {villageName} Portal...</p>
        </div>
      </div>
    );
  }

  // ── Not Published / Not Created ──────────────────────────────────────────
  if (!portal || !portal.isPublished) {
    return (
      <div style={{ minHeight:"100vh", background:"#070D0A", display:"flex", flexDirection:"column" as const }}>
        <div style={{ background:"#0E1812", borderBottom:"1px solid rgba(201,168,76,0.2)", padding:"16px 24px" }}>
          <button onClick={onBack}
            style={{ color:"#C9A84C", display:"flex", alignItems:"center", gap:8, fontSize:13, background:"none", border:"none", cursor:"pointer" }}>
            <ArrowLeft className="w-4 h-4" />
            Back to Communities
          </button>
        </div>
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:32 }}>
          <div style={{ textAlign:"center" as const, maxWidth:440 }}>
            <div style={{ width:96, height:96, borderRadius:"50%", background:"rgba(201,168,76,0.08)", border:"2px solid rgba(201,168,76,0.25)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 24px" }}>
              <Crown style={{ width:48, height:48, color:"rgba(201,168,76,0.5)" }} />
            </div>
            <h2 style={{ fontFamily:"Cinzel,serif", fontSize:28, fontWeight:700, color:"#EDE8DC", marginBottom:8 }}>
              {villageName}
            </h2>
            <p style={{ color:"#C9A84C", fontWeight:600, marginBottom:16 }}>Community Portal</p>
            <div style={{ background:"#121E16", borderRadius:12, padding:24, border:"1px solid #1C2E22", marginBottom:24 }}>
              <Lock style={{ width:32, height:32, color:"rgba(237,232,220,0.2)", margin:"0 auto 12px" }} />
              <p style={{ color:"#EDE8DC", fontWeight:600, marginBottom:8 }}>Portal Under Development</p>
              <p style={{ color:"rgba(237,232,220,0.4)", fontSize:13, lineHeight:1.6 }}>
                The {villageName} community portal is being prepared.
                Community administrators can log in to begin uploading content.
                It will be visible once published.
              </p>
            </div>
            {isAdmin && onAdminAccess && (
              <button onClick={onAdminAccess}
                style={{ padding:"10px 24px", background:"#0F5132", border:"1px solid rgba(201,168,76,0.4)", borderRadius:10, color:"#C9A84C", fontWeight:700, display:"flex", alignItems:"center", gap:8, margin:"0 auto", cursor:"pointer" }}>
                <Edit3 className="w-4 h-4" />
                Set Up This Portal
              </button>
            )}
            <button onClick={onBack}
              style={{ marginTop:16, color:"rgba(237,232,220,0.3)", fontSize:13, background:"none", border:"none", cursor:"pointer", display:"block", margin:"16px auto 0" }}>
              ← Return to all communities
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Published Portal ─────────────────────────────────────────────────────
  const featuredImages = (portal.media || []).filter(
    (m: any) => m.isFeatured && m.mediaType === 'IMAGE'
  );
  const filteredMedia = (portal.media || []).filter(
    (m: any) => galleryFilter === 'ALL' || m.mediaType === galleryFilter
  );
  const publishedPosts = (portal.posts || []).filter((p: any) => p.status === 'PUBLISHED');
  const activeAnnouncements = (portal.announcements || []).filter(
    (a: any) => a.isActive && (!a.expiresAt || new Date(a.expiresAt) > new Date())
  );

  return (
    <div style={{ minHeight:"100vh", background:"#070D0A", color:"#EDE8DC" }}>
      {/* Media Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}>
          <div className="bg-slate-900 rounded-2xl max-w-3xl w-full p-6 border border-amber-500/30"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ color:"#C9A84C", fontWeight:600 }}>{selectedMedia.title}</h3>
              <button onClick={() => setSelectedMedia(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            {selectedMedia.mediaType === 'IMAGE' && (
              <img src={selectedMedia.url} alt={selectedMedia.title}
                className="w-full rounded-xl max-h-96 object-contain" />
            )}
            {selectedMedia.mediaType === 'VIDEO' && (
              <video src={selectedMedia.url} controls
                className="w-full rounded-xl max-h-96"
                poster={selectedMedia.thumbnailUrl} />
            )}
            {selectedMedia.mediaType === 'AUDIO' && (
              <div className="bg-slate-800 rounded-xl p-8 text-center">
                <Music className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                <p className="text-white font-semibold mb-4">{selectedMedia.title}</p>
                <audio src={selectedMedia.url} controls className="w-full" />
              </div>
            )}
            {selectedMedia.caption && (
              <p className="text-slate-400 text-sm mt-3 text-center italic">{selectedMedia.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* Top Nav */}
      <div className="bg-slate-900/95 backdrop-blur border-b border-amber-500/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack}
            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Communities</span>
          </button>
          <div className="text-center">
            <p className="text-xs text-amber-500 font-medium uppercase tracking-widest">Community Portal</p>
            <h1 className="text-sm font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
              {portal.portalTitle || villageName}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleShare}
              className="text-slate-400 hover:text-amber-400 transition-colors relative p-1">
              <Share2 className="w-4 h-4" />
              {shareMsg && (
                <span className="absolute -bottom-7 right-0 text-xs bg-slate-800 text-amber-400 px-2 py-1 rounded whitespace-nowrap">
                  {shareMsg}
                </span>
              )}
            </button>
            {isAdmin && onAdminAccess && (
              <button onClick={onAdminAccess}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-xs font-medium border border-amber-500/30 transition-colors">
                <Edit3 className="w-3 h-3" />
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-72 sm:h-96 overflow-hidden">
        {featuredImages.length > 0 ? (
          featuredImages.map((img: any, i: number) => (
            <div key={img.id} className="absolute inset-0 transition-opacity duration-1000"
              style={{ opacity: i === currentSlide ? 1 : 0 }}>
              <img src={img.url} alt={img.title} className="w-full h-full object-cover" />
            </div>
          ))
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
          {portal.geographicZone && (
            <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-1">
              {portal.geographicZone} Zone · Uzairue Kingdom
            </p>
          )}
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-2"
            style={{ fontFamily: 'Cinzel, serif' }}>
            {portal.portalTitle || villageName}
          </h1>
          {portal.tagline && (
            <p className="text-amber-300 italic">"{portal.tagline}"</p>
          )}
          <div className="flex flex-wrap gap-4 mt-3">
            {portal.population > 0 && (
              <div className="flex items-center gap-1.5 text-slate-300 text-sm">
                <Users className="w-4 h-4 text-amber-400" />
                {portal.population.toLocaleString()} residents
              </div>
            )}
            {portal.foundingYear && (
              <div className="flex items-center gap-1.5 text-slate-300 text-sm">
                <Calendar className="w-4 h-4 text-amber-400" />
                Est. {portal.foundingYear}
              </div>
            )}
          </div>
          {featuredImages.length > 1 && (
            <div className="flex gap-1.5 mt-3">
              {featuredImages.map((_: any, i: number) => (
                <button key={i} onClick={() => setCurrentSlide(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === currentSlide ? 'bg-amber-400 w-6' : 'bg-white/30 w-1.5'
                  }`} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Announcements */}
      {activeAnnouncements.length > 0 && (
        <div className="bg-amber-500/10 border-y border-amber-500/20">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-start gap-3">
            <Megaphone className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              {activeAnnouncements.slice(0, 2).map((ann: any) => (
                <div key={ann.id} className="flex items-start gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${
                    ann.priority === 'URGENT' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                    ann.priority === 'HIGH' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                    'bg-blue-500/20 text-blue-400 border-blue-500/30'
                  }`}>{ann.priority}</span>
                  <span className="text-slate-300 text-sm">
                    <span className="text-amber-300 font-semibold">{ann.title}: </span>
                    {ann.content}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-slate-800 bg-slate-900/50 sticky top-[57px] z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-1" style={{ scrollbarWidth: 'none' }}>
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                    activeTab === tab.id
                      ? 'border-amber-400 text-amber-400'
                      : 'border-transparent text-slate-500 hover:text-slate-300'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* HOME */}
        {activeTab === 'home' && (
          <div className="space-y-8">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {portal.welcomeMessage && (
                  <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
                    <h2 className="text-xl font-bold text-amber-400 mb-3"
                      style={{ fontFamily: 'Playfair Display, serif' }}>
                      Welcome to {villageName}
                    </h2>
                    <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {portal.welcomeMessage}
                    </p>
                  </div>
                )}
                {portal.culturalSignificance && (
                  <div className="bg-gradient-to-br from-emerald-950/50 to-slate-900 rounded-2xl p-6 border border-emerald-800/30">
                    <h3 className="text-lg font-bold text-emerald-400 mb-2 flex items-center gap-2">
                      <Star className="w-5 h-5" />
                      Cultural Significance
                    </h3>
                    <p className="text-slate-300 leading-relaxed">{portal.culturalSignificance}</p>
                  </div>
                )}
              </div>
              {portal.traditionalRuler && (
                <div className="bg-gradient-to-b from-amber-950/40 to-slate-900 rounded-2xl p-6 border border-amber-500/20 text-center">
                  {portal.rulerImageUrl ? (
                    <img src={portal.rulerImageUrl} alt={portal.traditionalRuler}
                      className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-2 border-amber-500/40" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-amber-500/10 border-2 border-amber-500/30 flex items-center justify-center mx-auto mb-4">
                      <Crown className="w-10 h-10 text-amber-500/60" />
                    </div>
                  )}
                  <p className="text-xs text-amber-500 uppercase tracking-widest mb-1">Traditional Ruler</p>
                  <p className="text-white font-bold text-sm">{portal.traditionalRuler}</p>
                  {portal.rulerTitle && <p className="text-amber-400 text-xs mt-1">{portal.rulerTitle}</p>}
                  {portal.rulerMessage && (
                    <p className="text-slate-400 text-xs mt-3 italic leading-relaxed">
                      "{portal.rulerMessage}"
                    </p>
                  )}
                </div>
              )}
            </div>
            {(portal.landmarks || []).length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-amber-400" />
                  Notable Landmarks
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {portal.landmarks.map((l: string, i: number) => (
                    <div key={i} className="bg-slate-900 rounded-xl p-4 border border-slate-800 flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {portal.culturalTroupeName && (
              <div className="bg-gradient-to-r from-emerald-950/40 to-slate-900 rounded-2xl p-6 border border-emerald-800/20">
                <h3 className="text-lg font-bold text-emerald-400 mb-2 flex items-center gap-2">
                  <Music className="w-5 h-5" />
                  Festival Cultural Troupe
                </h3>
                <p className="text-white font-semibold text-lg">{portal.culturalTroupeName}</p>
                {portal.performanceDescription && (
                  <p className="text-slate-400 mt-2">{portal.performanceDescription}</p>
                )}
              </div>
            )}
            {publishedPosts.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Latest from {villageName}</h3>
                  <button onClick={() => setActiveTab('news')}
                    className="text-amber-400 text-sm hover:text-amber-300">View all →</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {publishedPosts.slice(0, 2).map((post: any) => (
                    <div key={post.id}
                      className="bg-slate-900 rounded-xl overflow-hidden border border-slate-800 hover:border-amber-500/30 transition-colors">
                      {post.featuredImageUrl && (
                        <img src={post.featuredImageUrl} alt={post.title}
                          className="w-full h-40 object-cover" />
                      )}
                      <div className="p-4">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
                          {(post.type || 'NEWS').replace('_', ' ')}
                        </span>
                        <h4 className="text-white font-semibold mt-2 line-clamp-2">{post.title}</h4>
                        <p className="text-slate-500 text-xs mt-1">
                          {post.authorName} · {post.publishedAt
                            ? new Date(post.publishedAt).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* HERITAGE */}
        {activeTab === 'heritage' && (
          <div className="space-y-8 max-w-4xl">
            <h2 className="text-2xl font-bold text-amber-400"
              style={{ fontFamily: 'Playfair Display, serif' }}>
              Cultural Heritage of {villageName}
            </h2>
            {portal.historicalBackground ? (
              <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {portal.historicalBackground}
                </p>
              </div>
            ) : (
              <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 text-center">
                <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">Historical background coming soon</p>
              </div>
            )}
            {(portal.festivals || []).length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Traditional Festivals</h3>
                <div className="flex flex-wrap gap-3">
                  {portal.festivals.map((f: string, i: number) => (
                    <span key={i}
                      className="px-4 py-2 bg-emerald-900/40 text-emerald-400 rounded-full border border-emerald-700/40 text-sm font-medium">
                      🎭 {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* GALLERY */}
        {activeTab === 'gallery' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Community Gallery</h2>
              <div className="flex gap-2">
                {(['ALL','IMAGE','VIDEO','AUDIO'] as const).map(f => (
                  <button key={f} onClick={() => setGalleryFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      galleryFilter === f
                        ? 'bg-amber-500 text-black'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}>
                    {f === 'ALL' ? '📁 All' : f === 'IMAGE' ? '🖼️ Photos' :
                     f === 'VIDEO' ? '🎬 Video' : '🎵 Audio'}
                  </button>
                ))}
              </div>
            </div>
            {filteredMedia.length === 0 ? (
              <div className="text-center py-16">
                <Camera className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">No media in this category yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredMedia.map((item: any) => (
                  <button key={item.id} onClick={() => setSelectedMedia(item)}
                    className="group relative aspect-square bg-slate-900 rounded-xl overflow-hidden border border-slate-800 hover:border-amber-500/40 transition-all">
                    {item.mediaType === 'IMAGE' ? (
                      <img src={item.thumbnailUrl || item.url} alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-800">
                        {item.mediaType === 'VIDEO' ? <Video className="w-10 h-10 text-slate-600" /> :
                         item.mediaType === 'AUDIO' ? <Mic className="w-10 h-10 text-slate-600" /> :
                         <FileText className="w-10 h-10 text-slate-600" />}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      {item.mediaType === 'VIDEO' ? <Play className="w-10 h-10 text-white" /> :
                       item.mediaType === 'AUDIO' ? <Mic className="w-10 h-10 text-white" /> :
                       <ExternalLink className="w-8 h-8 text-white" />}
                    </div>
                    {item.isFeatured && (
                      <div className="absolute top-2 right-2">
                        <Star className="w-3.5 h-3.5 text-amber-400" fill="currentColor" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NEWS */}
        {activeTab === 'news' && (
          <div className="space-y-6 max-w-4xl">
            <h2 className="text-2xl font-bold text-white">Community News & Stories</h2>
            {publishedPosts.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">No posts published yet</p>
              </div>
            ) : publishedPosts.map((post: any) => (
              <article key={post.id}
                className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 hover:border-amber-500/20 transition-colors">
                {post.featuredImageUrl && (
                  <img src={post.featuredImageUrl} alt={post.title}
                    className="w-full h-56 object-cover" />
                )}
                <div className="p-6">
                  <span className="text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    {(post.type || 'NEWS').replace('_', ' ')}
                  </span>
                  <h3 className="text-xl font-bold text-white mt-3 mb-2">{post.title}</h3>
                  <p className="text-slate-400">{post.summary}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                    <span style={{ color:"rgba(237,232,220,0.4)", fontSize:13, lineHeight:1.6 }}>By {post.authorName || 'Community Team'}</span>
                    <span className="text-slate-600 text-xs">{post.viewCount || 0} views</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div className="space-y-6 max-w-4xl">
            <h2 className="text-2xl font-bold text-white">Historical Timeline</h2>
            {(portal.timeline || []).length === 0 ? (
              <div className="text-center py-16">
                <Clock className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">Historical timeline being compiled</p>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-amber-500/20" />
                <div className="space-y-8 pl-16">
                  {[...(portal.timeline || [])].sort(
                    (a: any, b: any) => (a.decade || 0) - (b.decade || 0)
                  ).map((event: any) => (
                    <div key={event.id} className="relative">
                      <div className={`absolute -left-[42px] w-4 h-4 rounded-full border-2 ${
                        event.significance === 'HIGH'
                          ? 'border-amber-400 bg-amber-400/30'
                          : 'border-slate-600 bg-slate-800'
                      }`} />
                      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-amber-400 font-bold text-lg">{event.year}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            event.category === 'CULTURE' ? 'bg-purple-500/20 text-purple-400' :
                            event.category === 'FESTIVAL' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>{event.category}</span>
                          {event.isVerified && <span className="text-xs text-green-400">✓ Verified</span>}
                        </div>
                        <h4 className="text-white font-semibold">{event.title}</h4>
                        <p className="text-slate-400 text-sm mt-1">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TEAM */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Community Leadership & Team</h2>
            {(portal.members || []).filter((m: any) => m.isActive).length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">Team directory coming soon</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {portal.members.filter((m: any) => m.isActive).map((member: any) => (
                  <div key={member.id}
                    className="bg-slate-900 rounded-2xl p-5 border border-slate-800 text-center hover:border-amber-500/30 transition-colors">
                    {member.imageUrl ? (
                      <img src={member.imageUrl} alt={member.fullName}
                        className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-2 border-amber-500/30" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center mx-auto mb-3">
                        <Users className="w-8 h-8 text-slate-600" />
                      </div>
                    )}
                    {member.title && <p className="text-amber-400 text-xs">{member.title}</p>}
                    <p className="text-white font-semibold mt-1">{member.fullName}</p>
                    <p className="text-slate-500 text-xs mt-1">
                      {member.position || member.role.replace('_', ' ')}
                    </p>
                    {member.bio && (
                      <p className="text-slate-400 text-xs mt-2 line-clamp-3">{member.bio}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CONTACT */}
        {activeTab === 'contact' && (
          <div className="space-y-6 max-w-2xl">
            <h2 className="text-2xl font-bold text-white">Contact {villageName}</h2>
            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 space-y-5">
              {portal.postalAddress && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-amber-400 text-sm font-medium">Address</p>
                    <p className="text-slate-300">{portal.postalAddress}</p>
                  </div>
                </div>
              )}
              {portal.contactPhone && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-green-400 text-sm font-medium">Phone</p>
                    <a href={`tel:${portal.contactPhone}`}
                      className="text-slate-300 hover:text-white transition-colors">
                      {portal.contactPhone}
                    </a>
                  </div>
                </div>
              )}
              {portal.contactEmail && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-blue-400 text-sm font-medium">Email</p>
                    <a href={`mailto:${portal.contactEmail}`}
                      className="text-slate-300 hover:text-white transition-colors">
                      {portal.contactEmail}
                    </a>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-purple-400 text-sm font-medium">Location</p>
                  <p className="text-slate-300">
                    {portal.geographicZone ? `${portal.geographicZone} Zone · ` : ''}
                    Uzairue Kingdom · Etsako West · Edo State · Nigeria
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 border-t border-slate-800 py-6 text-center">
        <p className="text-slate-600 text-sm">{villageName} · Uzairue Kingdom · ERUE Festival 2026</p>
        <p className="text-slate-700 text-xs mt-1">One People, One Kingdom, One Destiny</p>
      </div>
    </div>
  );
};

export default CommunitySubPortal;
